import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { toDisplay } from "../lib/roles";
import { Empty, Field, Loading, PageHeader, Panel } from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS = { valida: ["Valida", "tag-green"], in_scadenza: ["In scadenza", "tag-amber"], scaduta: ["Scaduta", "tag-red"] };

function NewDialog({ sectors, onClose, onSaved }) {
  const [f, setF] = useState({ settore: sectors.length === 1 ? sectors[0] : "", data: "", descrizione: "", note: "", scad: "", avviso: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.settore) return toast.error("Seleziona il settore");
    if (!f.descrizione.trim()) return toast.error("Inserisci la descrizione");
    if (!f.scad) return toast.error("Inserisci la data di scadenza");
    try {
      await api("/scadenziario", { method: "POST", body: { settore: f.settore, data: toDisplay(f.data), descrizione: f.descrizione.trim(), note: f.note.trim(), data_scadenza: toDisplay(f.scad), avviso_giorni: parseInt(f.avviso || "0", 10) || 0 } });
      toast.success("Scadenza aggiunta"); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Nuova scadenza</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <Field label="Settore"><Select value={f.settore} onValueChange={(v) => setF({ ...f, settore: v })}><SelectTrigger data-testid="scad-sector-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger><SelectContent>{sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Data"><Input type="date" value={f.data} onChange={set("data")} data-testid="scad-date-input" /></Field>
        <Field label="Descrizione"><Input value={f.descrizione} onChange={set("descrizione")} placeholder="Es. Rinnovo licenza" data-testid="scad-desc-input" /></Field>
        <Field label="Note"><Textarea value={f.note} onChange={set("note")} placeholder="Note aggiuntive" data-testid="scad-note-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data scadenza"><Input type="date" value={f.scad} onChange={set("scad")} data-testid="scad-due-input" /></Field>
          <Field label="Avviso (giorni prima)"><Input type="number" value={f.avviso} onChange={set("avviso")} placeholder="Es. 30" data-testid="scad-notice-input" /></Field>
        </div>
        <Button onClick={save} className="w-full rounded-full bg-[var(--teal)]" data-testid="scad-save-button">Salva scadenza</Button>
      </div></DialogContent></Dialog>
  );
}

export default function Scadenziario() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api("/scadenziario").then(setD).catch(() => setD({ sectors: [], items: [] })), []);
  useEffect(() => { load(); }, [load]);
  const del = async (it) => {
    if (!window.confirm(`Eliminare la scadenza "${it.descrizione}"?`)) return;
    try { await api(`/scadenziario/${it.id}`, { method: "DELETE" }); toast.success("Scadenza eliminata"); load(); } catch (e) { toast.error(e.message); }
  };
  if (!d) return <Loading />;
  return (
    <div>
      <PageHeader subtitle="Uffici" title="Scadenziario" actions={<Button onClick={() => setOpen(true)} className="rounded-full bg-[var(--teal)]" data-testid="scad-new-button"><Plus className="w-4 h-4 mr-1" />Nuova scadenza</Button>} />
      {d.items.length === 0 ? <Empty title="Nessuna scadenza" text="Premi “Nuova scadenza” per aggiungerne una." /> : (
        <div className="space-y-3 stagger" data-testid="scad-list">{d.items.map((it) => (
          <Panel key={it.id} className="!p-5 flex justify-between gap-4" data-testid={`scad-${it.id}`}>
            <div><div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-[var(--navy)]">{it.descrizione}</span><span className="tag tag-teal">{it.settore}</span>{STATUS[it.scadenza_status] && <span className={`tag ${STATUS[it.scadenza_status][1]}`}>{STATUS[it.scadenza_status][0]}</span>}</div>
              {it.note && <p className="text-sm text-slate-600 mt-1">{it.note}</p>}
              <p className="text-xs text-slate-400 mt-2">{it.data_it ? `${it.data_it} · ` : ""}Scadenza {it.data_scadenza_it || it.data_scadenza}{it.avviso_giorni ? ` · avviso ${it.avviso_giorni} gg prima` : ""}</p></div>
            <button onClick={() => del(it)} className="text-slate-400 hover:text-[var(--red)] h-fit" data-testid={`scad-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
          </Panel>))}
        </div>)}
      {open && <NewDialog sectors={d.sectors || []} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
